export interface BalanceProviderLike {
  provider: string
  balance: number
  limit: number
  status: string
  stale: boolean
}

export interface ProviderSnapshotLike {
  status: string
  lastUpdated: string | null
}

export type BalanceStatusTone = 'good' | 'danger' | 'stale' | 'error'
export type ProviderChartTone = 'primary' | 'secondary'
export type ChartTooltipAlignment = 'start' | 'center' | 'end'

export interface ChartTooltipPlacement {
  align: ChartTooltipAlignment
  maxWidthPercent: number
}

export function chartTooltipPlacement(leftPercent: number): ChartTooltipPlacement {
  const safeLeft = Math.min(100, Math.max(0, leftPercent))
  if (safeLeft <= 50) {
    return { align: 'start', maxWidthPercent: 100 - safeLeft }
  }
  return { align: 'end', maxWidthPercent: safeLeft }
}

export function focusableChartIndexes(itemCount: number, maximum = 30): number[] {
  const count = Math.max(0, Math.floor(itemCount))
  const limit = Math.max(1, Math.floor(maximum))
  if (count === 0) return []
  if (count <= limit) return Array.from({ length: count }, (_, index) => index)
  if (limit === 1) return [0]

  return Array.from(
    { length: limit },
    (_, index) => Math.round((index * (count - 1)) / (limit - 1)),
  )
}

export function timeChartPositionShares(takenAt: string[]): number[] {
  if (takenAt.length === 0) return []
  if (takenAt.length === 1) return [0.5]

  const timestamps = takenAt.map((value) => Date.parse(value))
  if (timestamps.some((value) => !Number.isFinite(value))) {
    return indexPositionShares(takenAt.length)
  }

  const minimum = Math.min(...timestamps)
  const maximum = Math.max(...timestamps)
  if (minimum === maximum) return indexPositionShares(takenAt.length)

  return timestamps.map((value) => (value - minimum) / (maximum - minimum))
}

export function timeChartLabelIndexes(takenAt: string[], maximumLabels = 3): number[] {
  const count = takenAt.length
  const limit = Math.max(1, Math.floor(maximumLabels))
  if (count <= limit) return Array.from({ length: count }, (_, index) => index)

  const timestamps = takenAt.map((value) => Date.parse(value))
  if (timestamps.some((value) => !Number.isFinite(value))) {
    return representativeIndexLabels(count, limit)
  }

  const minimum = Math.min(...timestamps)
  const maximum = Math.max(...timestamps)
  if (minimum === maximum) return representativeIndexLabels(count, limit)
  if (limit === 1) {
    const midpoint = minimum + (maximum - minimum) / 2
    return [nearestTimestampIndex(timestamps, midpoint, new Set())]
  }

  const selected = new Set<number>()
  return Array.from({ length: limit }, (_, labelIndex) => {
    const target = minimum + ((maximum - minimum) * labelIndex) / (limit - 1)
    const index = nearestTimestampIndex(timestamps, target, selected)
    selected.add(index)
    return index
  })
}

function indexPositionShares(count: number): number[] {
  return Array.from({ length: count }, (_, index) => index / Math.max(1, count - 1))
}

function representativeIndexLabels(count: number, maximumLabels: number): number[] {
  if (maximumLabels === 1) return [Math.floor((count - 1) / 2)]
  return Array.from(
    { length: maximumLabels },
    (_, index) => Math.round((index * (count - 1)) / (maximumLabels - 1)),
  )
}

function nearestTimestampIndex(
  timestamps: number[],
  target: number,
  excluded: ReadonlySet<number>,
): number {
  let nearest = -1
  let nearestDistance = Number.POSITIVE_INFINITY

  for (let index = 0; index < timestamps.length; index += 1) {
    if (excluded.has(index)) continue
    const distance = Math.abs((timestamps[index] ?? target) - target)
    if (distance < nearestDistance) {
      nearest = index
      nearestDistance = distance
    }
  }

  return nearest < 0 ? 0 : nearest
}

export function isCompleteBalanceResponse(value: unknown): boolean {
  return Boolean(
    isRecord(value) &&
      (value.days === 7 || value.days === 30 || value.days === 90) &&
      finite(value.elevenLabsUsdPer1kCredits) &&
      value.elevenLabsUsdPer1kCredits > 0 &&
      Array.isArray(value.providers) &&
      value.providers.every(isCompleteBalanceProvider),
  )
}

export function isCompleteBalanceProvider(value: unknown): boolean {
  if (!isRecord(value)) return false
  if (typeof value.provider !== 'string' || value.provider.trim() === '') return false
  if (typeof value.status !== 'string' || value.status.trim() === '') return false
  if (!finite(value.balance) || !finite(value.used) || !finite(value.limit)) return false
  if (typeof value.stale !== 'boolean') return false
  if (!nullableString(value.nextReset) || !nullableString(value.lastUpdated)) return false
  if (!nullableNumber(value.forecastDays) || !finite(value.balanceUsd)) return false
  if (!finite(value.spendUsd)) return false
  if (!Array.isArray(value.series) || !Array.isArray(value.dailySpend)) return false
  return value.series.every(isCompleteSeriesPoint) && value.dailySpend.every(isCompleteSpendPoint)
}

export function providerChartTone(provider: string): ProviderChartTone {
  return provider.toLowerCase() === 'elevenlabs' ? 'secondary' : 'primary'
}

// Statuses for which the API still delivers a real snapshot (healthy, overdue, or
// failing with last-known data). Anything else — including 'unknown' and any
// unrecognized value — has no trustworthy money figures and renders as "Нет данных".
const SNAPSHOT_STATUSES = new Set(['ok', 'active', 'healthy', 'past_due', 'down', 'error', 'unavailable'])

export function providerSnapshotAvailable(provider: ProviderSnapshotLike): boolean {
  return (
    SNAPSHOT_STATUSES.has(provider.status.trim().toLowerCase()) &&
    typeof provider.lastUpdated === 'string' &&
    provider.lastUpdated.trim() !== ''
  )
}

export function providerRemainingRatio(provider: BalanceProviderLike): number | null {
  if (!Number.isFinite(provider.balance) || !Number.isFinite(provider.limit) || provider.limit <= 0) {
    return null
  }
  return provider.balance / provider.limit
}

export function balanceStatusTone(provider: BalanceProviderLike): BalanceStatusTone {
  const status = provider.status.toLowerCase()
  if (status === 'down' || status === 'error' || status === 'unavailable') return 'error'
  if (status === 'past_due' || provider.balance < 0) return 'danger'
  const ratio = providerRemainingRatio(provider)
  if (ratio !== null && ratio < 0.1) return 'danger'
  if (provider.stale) return 'stale'
  if (status === 'ok' || status === 'active' || status === 'healthy') return 'good'
  return 'error'
}

export function balanceAlert(provider: BalanceProviderLike): string | null {
  if (provider.provider.toLowerCase() === 'elevenlabs' && provider.status.toLowerCase() === 'past_due') {
    return 'Счёт не оплачен — голос не работает'
  }
  return null
}

export function providerStatusLabel(provider: BalanceProviderLike): string {
  const status = provider.status.toLowerCase()
  if (status === 'past_due') return 'Не оплачен'
  if (provider.balance < 0) return 'Минусовой баланс'
  const tone = balanceStatusTone(provider)
  if (tone === 'error') return 'Недоступен'
  if (tone === 'stale') return 'Устарело'
  if (tone === 'danger') return 'Мало средств'
  return 'Работает'
}

export function providerDisplayName(provider: string): string {
  const normalized = provider.toLowerCase()
  if (normalized === 'openrouter') return 'OpenRouter'
  if (normalized === 'elevenlabs') return 'ElevenLabs'
  return provider
}

function isCompleteSeriesPoint(value: unknown): boolean {
  return Boolean(
    isRecord(value) &&
      typeof value.takenAt === 'string' &&
      finite(value.balance) &&
      finite(value.used) &&
      finite(value.limit) &&
      typeof value.source === 'string',
  )
}

function isCompleteSpendPoint(value: unknown): boolean {
  return Boolean(
    isRecord(value) &&
      typeof value.date === 'string' &&
      finite(value.value) &&
      typeof value.source === 'string' &&
      typeof value.label === 'string',
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function nullableNumber(value: unknown): boolean {
  return value === null || finite(value)
}

function nullableString(value: unknown): boolean {
  return value === null || typeof value === 'string'
}
